'use client';

import { useState } from 'react';
import { Close } from '@ghosts-inc/design-system/icons';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { BuyerListingDislikeReason } from '@/app/(authenticated)/(marketplace)/types';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: ({
    customReason,
    reasonType,
  }: {
    customReason: string | null;
    reasonType: BuyerListingDislikeReason;
  }) => Promise<void>;
};

export default function CustomFeedbackDialog({ isOpen, onClose, onSubmit }: Props) {
  const [customFeedback, setCustomFeedback] = useState('');

  const handleSubmit = async () => {
    await onSubmit({
      customReason: customFeedback,
      reasonType: BuyerListingDislikeReason.OTHER,
    });
    handleClose();
  };

  const handleClose = () => {
    setCustomFeedback('');
    onClose();
  };

  return (
    <Dialog
      onClose={handleClose}
      open={isOpen}
      fullWidth
      sx={{
        '.MuiDialog-paper': {
          borderRadius: { mobile: 0, smallTablet: '12px' },
          height: { mobile: '100%', smallTablet: 'auto' },
          margin: { mobile: 0, smallTablet: 4 },
          maxHeight: 'unset',
          maxWidth: { mobile: 'unset', smallTablet: 400 },
          padding: 0,
          width: '100%',
        },
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center" p={3}>
        <Typography variant="h3">We&apos;re listening...</Typography>
        <IconButton aria-label="close" onClick={handleClose}>
          <Close data-testid="close-icon" sx={{ fontSize: 28 }} />
        </IconButton>
      </Stack>
      <Divider sx={{ '&.MuiDivider-root': { borderColor: 'lightBorder.default' } }} />
      <Stack height="100%" justifyContent="space-between" p={3}>
        <TextField
          variant="outlined"
          label="Let us know why you're not interested"
          placeholder="High MOQ, Inventory not right, brand not selling well, shipping too long, etc..."
          value={customFeedback}
          onChange={(e) => setCustomFeedback(e.target.value)}
          multiline
          rows={4}
        />
      </Stack>
      <Divider sx={{ '&.MuiDivider-root': { borderColor: 'lightBorder.default' } }} />
      <Stack justifyContent="space-between" p={3}>
        <DialogActions sx={{ pt: 0, mt: 0 }}>
          <Button variant="outlined" onClick={handleClose}>
            Cancel
          </Button>
          <Button onClick={handleSubmit}>Send Feedback</Button>
        </DialogActions>
      </Stack>
    </Dialog>
  );
}
